<template>
  <div class="animated fadeIn">
    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0">Log Access Trail</h4>
        <small class="text-muted">Who read or downloaded which log file, and how many bytes were served.</small>
      </b-col>
      <b-col cols="auto">
        <b-button size="sm" variant="outline-secondary" to="/application-logs/explorer">
          <i class="fa fa-arrow-left mr-1"></i>Back to logs
        </b-button>
      </b-col>
    </b-row>

    <b-alert :show="!available" variant="warning" class="py-2">
      The trail needs the <code>tipsoi_admin</code> datasource. Access is still being recorded in the
      application log itself.
    </b-alert>

    <b-card class="mb-3 shadow-sm">
      <b-form @submit.prevent="apply">
        <b-form-row>
          <b-col sm="6" md="3" class="mb-2">
            <label class="filter-label">Actor</label>
            <b-form-input v-model="filters.actor" size="sm" placeholder="username" />
          </b-col>
          <b-col sm="6" md="3" class="mb-2">
            <label class="filter-label">Action</label>
            <b-form-select v-model="filters.action" :options="actionOptions" size="sm" />
          </b-col>
          <b-col sm="12" md="4" class="mb-2 d-flex align-items-end">
            <b-button type="submit" variant="primary" size="sm" class="mr-2" :disabled="loading">
              <i class="icon-magnifier mr-1"></i>Apply
            </b-button>
            <b-button type="button" variant="outline-secondary" size="sm" @click="clear">
              <i class="icon-close mr-1"></i>Clear
            </b-button>
          </b-col>
        </b-form-row>
      </b-form>
    </b-card>

    <div class="d-flex justify-content-between align-items-center mb-2">
      <small class="text-muted">Showing <strong>{{ records.length }}</strong> of <strong>{{ total }}</strong></small>
      <b-spinner v-if="loading" small variant="primary" />
    </div>

    <b-card no-body class="shadow-sm">
      <b-table :items="records" :fields="fields" :busy="loading" responsive striped hover show-empty
               empty-text="Nothing recorded yet." class="mb-0" small>
        <template #table-busy>
          <div class="text-center py-4"><b-spinner variant="primary" /> Loading…</div>
        </template>
        <template #cell(createdAt)="d"><span class="mono">{{ when(d.value) }}</span></template>
        <template #cell(action)="d">
          <b-badge :variant="actionVariant(d.value)">{{ d.value }}</b-badge>
        </template>
        <template #cell(fileName)="d"><span class="mono small">{{ d.value || '—' }}</span></template>
        <template #cell(bytesServed)="d"><span class="mono">{{ d.value ? humanSize(d.value) : '—' }}</span></template>
        <template #cell(detail)="d"><span class="small text-muted">{{ d.value || '' }}</span></template>
      </b-table>

      <div class="d-flex justify-content-center py-3" v-if="total > pageSize">
        <b-pagination v-model="currentPage" :total-rows="total" :per-page="pageSize"
                      @change="onPageChange" size="sm" pills hide-goto-end-buttons />
      </div>
    </b-card>
  </div>
</template>

<script>
import logService from '@/services/logService'
import toastService from '@/services/toastService'

export default {
  name: 'LogAccessAudit',
  data: function() {
    return {
      records: [], total: 0, available: true,
      currentPage: 1, pageSize: 50, loading: false,
      filters: { actor: '', action: '' },
      actionOptions: [
        { value: '', text: 'All actions' },
        { value: 'VIEW', text: 'View' },
        { value: 'SEARCH', text: 'Search' },
        { value: 'DOWNLOAD_FILTERED', text: 'Download (filtered)' },
        { value: 'DOWNLOAD_RAW', text: 'Download (raw)' },
        { value: 'CONFIG_CHANGE', text: 'Config change' }
      ],
      fields: [
        { key: 'createdAt', label: 'When', thStyle: 'width:150px' },
        { key: 'actor', label: 'Actor', thStyle: 'width:110px' },
        { key: 'action', label: 'Action', thStyle: 'width:150px' },
        { key: 'sourceCode', label: 'Source', thStyle: 'width:130px' },
        { key: 'fileName', label: 'File' },
        { key: 'detail', label: 'Detail' },
        { key: 'bytesServed', label: 'Bytes', thStyle: 'width:90px' },
        { key: 'clientIp', label: 'IP', thStyle: 'width:120px' }
      ]
    }
  },
  created: function() { this.fetch() },
  methods: {
    fetch: function() {
      var self = this
      self.loading = true
      logService.getAudit({
        actor: self.filters.actor || undefined,
        action: self.filters.action || undefined,
        page: self.currentPage - 1,
        size: self.pageSize
      }, function(data) {
        self.records = data.records || []
        self.total = data.total || 0
        self.available = data.available !== false
        self.loading = false
      }, function(msg) { self.loading = false; toastService.showErrorToast(msg) })
    },
    apply: function() { this.currentPage = 1; this.fetch() },
    clear: function() { this.filters = { actor: '', action: '' }; this.currentPage = 1; this.fetch() },
    onPageChange: function(page) { this.currentPage = page; this.$nextTick(this.fetch) },
    when: function(ms) {
      if (!ms) { return '' }
      var d = new Date(ms)
      var p = function(n) { return n < 10 ? '0' + n : '' + n }
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) +
             ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds())
    },
    actionVariant: function(action) {
      var map = {
        VIEW: 'secondary', SEARCH: 'info',
        DOWNLOAD_FILTERED: 'primary', DOWNLOAD_RAW: 'warning', CONFIG_CHANGE: 'dark'
      }
      return map[action] || 'secondary'
    },
    humanSize: function(bytes) {
      var units = ['B', 'KB', 'MB', 'GB', 'TB']
      var i = 0
      var n = bytes
      while (n >= 1024 && i < units.length - 1) { n = n / 1024; i++ }
      return (i === 0 ? n : n.toFixed(1)) + ' ' + units[i]
    }
  }
}
</script>

<style scoped>
.filter-label { font-size: 11px; font-weight: 600; color: #73818f; margin-bottom: 2px; }
.mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 11.5px; }
</style>
